<script setup lang="ts">
import { computed, type PropType, ref } from 'vue'
import {
  BellIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PhoneIcon,
} from '@heroicons/vue/24/outline'
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { useCallStore } from '@/stores/useCallStore'
import { storeToRefs } from 'pinia'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()
const callStore = useCallStore()

const { loadingUpdate } = storeToRefs(callStore)

type ItemType = {
  id: number
  date: Date
  formattedDate: string
  user: { id?: number; name?: string }
  comment?: string
  type: string
  doNotDisturb: boolean
  isOwner: boolean
}

const props = defineProps({
  items: {
    type: Array as PropType<ItemType[]>,
    required: true,
  },
})

const itemsFiltered = computed(() => {
  if (isTimelineOpen.value) {
    return props.items
  }
  return props.items.slice(0, 5)
})

const editingItemId = ref<number | null>(null)
const editingValueBefore = ref<string | null>(null)

const save = async function (itemId: number, comment: string | undefined) {
  if (comment) {
    if (await callStore.updateCall(itemId, { comment: comment })) {
      editingItemId.value = null
      $toast.success('Appel modifié')
    } else $toast.error('Erreur lors de la modification')
  }
}

const startEditing = (item: ItemType) => {
  editingItemId.value = item.id
  editingValueBefore.value = item.comment || ''
}

const cancelEditing = (item: ItemType) => {
  if (editingValueBefore.value) {
    item.comment = editingValueBefore.value
    editingItemId.value = null
    editingValueBefore.value = null
  }
}

const isTimelineOpen = ref(false)

const toggleTimeline = () => {
  isTimelineOpen.value = !isTimelineOpen.value
}
</script>

<template>
  <div class="relative">
    <ul class="timeline timeline-snap-icon max-md:timeline-compact timeline-vertical">
      <li v-for="(item, index) in itemsFiltered" :key="index" class="mb-2 min-h-[100px]">
        <div class="timeline-middle">
          <div
            v-if="item.type === 'event'"
            class="p-2 rounded-full flex items-center justify-center"
          >
            <CalendarIcon class="h-5 w-5 text-accent" />
          </div>
          <div
            :class="{
              'p-2 rounded-full flex items-center justify-center timeline-start md:text-end':
                !item.isOwner, // Affiche à gauche si ce n'est pas l'utilisateur connecté
              'p-2 rounded-full flex items-center justify-center timeline-end': item.isOwner, // Affiche à droite si c'est l'utilisateur connecté
            }"
            v-else-if="item.type === 'message'"
          >
            <BellIcon class="h-5 w-5 text-info" />
          </div>
          <div
            v-else-if="item.doNotDisturb"
            class="p-2 rounded-full flex items-center justify-center"
          >
            <component :is="NoSymbolIcon" class="h-5 w-5 text-error" />
          </div>
          <div v-else class="p-2 rounded-full flex items-center justify-center">
            <PhoneIcon class="h-5 w-5 text-primary" />
          </div>
        </div>
        <div
          :class="{
            'timeline-start md:text-end': !item.isOwner, // Affiche à gauche si ce n'est pas l'utilisateur connecté
            'timeline-end': item.isOwner, // Affiche à droite si c'est l'utilisateur connecté
          }"
          class="timeline-box mb-5"
        >
          <div v-if="editingItemId === item.id">
            <!-- Mode édition -->
            <textarea
              v-if="item.comment !== null"
              v-model="item.comment"
              class="w-96 min-h-60 p-2 rounded"
            ></textarea>
            <div class="flex justify-end gap-2 mt-2">
              <button-daisy
                v-if="!loadingUpdate"
                text="Sauvegarder"
                variant="primary"
                @click="save(item.id, item.comment)"
              ></button-daisy>
              <loader-center v-if="loadingUpdate" />
              <button-daisy text="Annuler" @click="cancelEditing(item)"></button-daisy>
            </div>
          </div>
          <div v-else class="flex justify-between gap-2">
            <p v-if="item.comment" v-html="item.comment"></p>
            <icon-with-tooltip
              class="ml-2"
              variant="ghost"
              text="Modifier"
              v-if="item.isOwner && item.type === 'call'"
              @click="startEditing(item)"
              :icon="PencilSquareIcon"
            />
          </div>
        </div>
        <div
          :class="{
            'mx-5': true,
            'timeline-start owner-name': item.isOwner, // Affiche à gauche si ce n'est pas l'utilisateur connecté
            'timeline-end owner-name': !item.isOwner, // Affiche à droite si c'est l'utilisateur connecté
          }"
        >
          <time class="font-mono italic text-nowrap">{{ item.formattedDate }}</time>
          <div :class="{ 'text-lg font-bold capitalize': true, 'text-end': item.isOwner }">
            {{ item.user.name }}
          </div>
        </div>
        <hr v-if="index !== items.length - 1" />
      </li>
    </ul>
    <ButtonDaisy
      :text="isTimelineOpen ? 'Masquer' : 'Afficher'"
      :icon="isTimelineOpen ? ChevronUpIcon : ChevronDownIcon"
      :class="{
        'z-2 w-fit': true,
        'absolute bottom-3 left-[calc(50%-45px)]': !isTimelineOpen,
        'sticky bottom-16 left-[calc(50%-358px)]': isTimelineOpen,
      }"
      @click="toggleTimeline"
    />
  </div>
</template>
<style>
.owner-name {
  grid-row-end: 3;
}

.timeline-end {
  z-index: 1;
}
</style>
